import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/api";
import usePageMeta from "../hooks/usePageMeta";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ArrowUpRight, Check, X, ExternalLink, CalendarClock } from "lucide-react";

const STATUS_STYLE = {
  Applied: "bg-[#E5E1DB] text-[#5C6B64]",
  Interviewing: "bg-[#FDF2DF] text-[#8B6A1F]",
  Offer: "bg-[#E2F0E7] text-[#1A362D]",
  Rejected: "bg-[#FCECEC] text-[#C65D47]",
};

export default function ShareView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  usePageMeta(
    data?.owner?.name ? `${data.owner.name}'s job search · Trackr` : "Shared pipeline · Trackr",
    data?.stats
      ? `${data.owner.name} has applied to ${data.stats.total} roles with a ${data.stats.response_rate}% response rate.`
      : "A shared job search pipeline, powered by Trackr."
  );

  useEffect(() => {
    (async () => {
      try {
        // Public endpoint — no credentials needed
        const { data } = await axios.get(`${API}/share/${token}`);
        setData(data);
      } catch (err) {
        setError(err.response?.status === 404 ? "This snapshot link is invalid or has been revoked." : "Couldn't load this snapshot.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F1EE]">
        <div className="w-8 h-8 border-2 border-[#1A362D] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F1EE] px-6">
        <div className="max-w-md text-center" data-testid="share-error">
          <h1 className="font-display text-4xl text-[#1A362D]">Snapshot unavailable</h1>
          <p className="text-[#5C6B64] mt-3">{error}</p>
          <Button asChild className="mt-6 rounded-full bg-[#1A362D] hover:bg-[#2C4A40] text-white">
            <Link to="/" data-testid="share-error-home-link">Visit Trackr</Link>
          </Button>
        </div>
      </div>
    );
  }

  const { owner, stats, applications } = data;

  return (
    <div className="min-h-screen bg-[#F4F1EE]" data-testid="share-view">
      {/* Header */}
      <header className="glass sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-[#1A362D]" data-testid="share-brand-link">
            <div className="w-7 h-7 rounded-full bg-[#1A362D] flex items-center justify-center">
              <span className="text-[#D99C3D] font-display font-bold text-sm">T</span>
            </div>
            <span className="font-display text-lg">Trackr</span>
          </Link>
          <Button asChild className="rounded-full bg-[#1A362D] hover:bg-[#2C4A40] text-white" data-testid="share-start-your-own-btn">
            <Link to="/register">Start your own <ArrowUpRight className="w-4 h-4 ml-1.5" /></Link>
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
        <p className="text-xs tracking-[0.2em] uppercase text-[#5C6B64]">Shared pipeline · Read-only</p>
        <h1 className="font-display text-4xl sm:text-5xl mt-2 text-[#1A362D]">
          {owner.name}'s job search
        </h1>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
          {[
            { label: "Applications", value: stats.total, tint: "#1A362D" },
            { label: "Response rate", value: `${stats.response_rate}%`, tint: "#D99C3D" },
            { label: "Interviews", value: stats.interviewed, tint: "#1A362D" },
            { label: "Offers", value: stats.offers, tint: "#1A362D" },
          ].map((s) => (
            <div key={s.label} className="rounded-2xl bg-white border border-[#E5E1DB] p-5">
              <p className="text-xs tracking-[0.2em] uppercase text-[#5C6B64]">{s.label}</p>
              <p className="font-display text-4xl mt-2" style={{ color: s.tint }}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Table */}
        <div className="mt-10 rounded-2xl bg-white border border-[#E5E1DB] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]" data-testid="share-applications-table">
              <thead>
                <tr className="text-left text-xs tracking-[0.15em] uppercase text-[#5C6B64] border-b border-[#E5E1DB] bg-[#FDFCFA]">
                  <th className="px-5 py-3.5">Company</th>
                  <th className="px-5 py-3.5">Role</th>
                  <th className="px-5 py-3.5">Source</th>
                  <th className="px-5 py-3.5">Pay</th>
                  <th className="px-5 py-3.5">Status</th>
                  <th className="px-5 py-3.5">Submitted</th>
                  <th className="px-5 py-3.5">Applied</th>
                </tr>
              </thead>
              <tbody>
                {applications.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-5 py-14 text-center text-[#5C6B64]">
                      No applications in this snapshot yet.
                    </td>
                  </tr>
                ) : applications.map((a) => (
                  <tr key={a.id} className="border-b border-[#F0EDE7] last:border-0 hover:bg-[#FDFCFA] transition-colors">
                    <td className="px-5 py-4 font-medium text-[#1A362D]">
                      {a.company}
                      {a.job_url && (
                        <a href={a.job_url} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 ml-2 text-xs text-[#5C6B64] hover:text-[#1A362D]">
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <div>{a.job_title}</div>
                      {a.status === "Interviewing" && a.interview_stage && (
                        <div className="text-xs text-[#8B6A1F] mt-0.5">{a.interview_stage}</div>
                      )}
                      {a.interview_date && (
                        <div className="text-xs text-[#8B6A1F] mt-0.5 inline-flex items-center gap-1">
                          <CalendarClock className="w-3 h-3" /> {a.interview_date}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-4 text-[#5C6B64]">{a.website || "—"}</td>
                    <td className="px-5 py-4 text-[#1A362D]">{a.pay_range || "—"}</td>
                    <td className="px-5 py-4">
                      <Badge className={`rounded-full ${STATUS_STYLE[a.status] || STATUS_STYLE.Applied} border-0`}>
                        {a.status}
                      </Badge>
                    </td>
                    <td className="px-5 py-4">
                      {a.submitted ? (
                        <span className="inline-flex items-center gap-1 text-[#1A362D]"><Check className="w-4 h-4" /> Yes</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[#5C6B64]"><X className="w-4 h-4" /> No</span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-[#5C6B64]">{a.date_applied || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Powered-by footer / CTA */}
        <div className="mt-10 rounded-3xl bg-[#1A362D] text-[#F4F1EE] p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-[#D99C3D] font-semibold">Powered by Trackr</p>
            <h2 className="font-display text-3xl mt-2">Track your own search.</h2>
            <p className="opacity-80 mt-2 max-w-md">
              The quiet, honest way to log every job application, follow-up, and interview — free up to 25 apps.
            </p>
          </div>
          <Button asChild size="lg" className="rounded-full bg-[#D99C3D] hover:bg-[#C48A2E] text-[#1A362D] font-semibold h-12 px-7" data-testid="share-cta-signup-btn">
            <Link to="/register">Start free <ArrowUpRight className="w-4 h-4 ml-1.5" /></Link>
          </Button>
        </div>
      </main>

      <footer className="border-t border-[#E5E1DB]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-6 text-center text-xs text-[#5C6B64]">
          Shared read-only snapshot · Powered by{" "}
          <Link to="/" className="underline underline-offset-4 text-[#1A362D]">Trackr</Link>
        </div>
      </footer>
    </div>
  );
}
